package com.example.histoire.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import io.ktor.client.HttpClient
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.attributes.InputType

private const val FORM_FOCUS_SCROLL_JS =
    "this.scrollIntoView({behavior:'smooth', block:'center'});"
private const val FORM_ENTER_NEXT_JS =
    "if(event.key==='Enter'){event.preventDefault();const scope=this.closest('[data-enter-scope]')||document;" +
        "const els=[...scope.querySelectorAll('input,button,textarea,select')]" +
        ".filter(e=>!e.disabled&&e.offsetParent!==null);" +
        "const i=els.indexOf(this);if(i>=0&&i<els.length-1){els[i+1].focus({preventScroll:true});" +
        "els[i+1].scrollIntoView({behavior:'smooth',block:'center'});}}"

private fun capitalizeLeadingLetter(value: String): String {
    val first = value.firstOrNull() ?: return value
    return if (first.isLetter()) {
        first.titlecaseChar() + value.drop(1)
    } else {
        value
    }
}

@Composable
fun ParentIdentityOnboarding(
    client: HttpClient,
    initialFirstName: String,
    initialLastName: String,
    initialContactEmail: String,
    onComplete: () -> Unit,
) {
    var firstName by remember { mutableStateOf(initialFirstName) }
    var lastName by remember { mutableStateOf(initialLastName) }
    var contactEmail by remember { mutableStateOf(initialContactEmail) }
    var error by remember { mutableStateOf<String?>(null) }
    var busy by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    Div({
        attr("data-enter-scope", "parent-identity")
        style { parentIdentityCard() }
    }) {
        Div({ style { parentIdentityHeroWrap() } }) {
            Img(src = "/histoire-auth-hero.png", attrs = {
                attr("alt", "Histoire du Soir — livre magique")
                style { parentIdentityHeroImg() }
            })
        }
        H1({ style { parentIdentityTitle() } }) {
            Text("Ton profil parent")
        }
        P({ style { parentIdentityLabel() } }) { Text("Prénom") }
        Input(type = InputType.Text, attrs = {
            value(firstName)
            onInput { firstName = capitalizeLeadingLetter(it.value) }
            attr("onfocus", FORM_FOCUS_SCROLL_JS)
            attr("onkeydown", FORM_ENTER_NEXT_JS)
            attr("placeholder", "Prénom")
            if (busy) attr("disabled", "true")
            style { parentIdentityInput() }
        })
        P({ style { parentIdentityLabel() } }) { Text("Nom") }
        Input(type = InputType.Text, attrs = {
            value(lastName)
            onInput { lastName = capitalizeLeadingLetter(it.value) }
            attr("onfocus", FORM_FOCUS_SCROLL_JS)
            attr("onkeydown", FORM_ENTER_NEXT_JS)
            attr("placeholder", "Nom")
            if (busy) attr("disabled", "true")
            style { parentIdentityInput() }
        })
        P({ style { parentIdentityLabel() } }) { Text("Adresse e-mail") }
        Input(type = InputType.Email, attrs = {
            value(contactEmail)
            onInput { contactEmail = it.value }
            attr("onfocus", FORM_FOCUS_SCROLL_JS)
            attr("onkeydown", FORM_ENTER_NEXT_JS)
            attr("placeholder", "toi@exemple.com")
            if (busy) attr("disabled", "true")
            style { parentIdentityInput() }
        })
        if (error != null) {
            P({ style { parentIdentityError() } }) { Text(error!!) }
        }
        Button(attrs = {
            attr("type", "button")
            if (busy) attr("disabled", "true")
            onClick {
                if (busy) return@onClick
                val fn = firstName.trim()
                val ln = lastName.trim()
                val em = contactEmail.trim()
                if (fn.isEmpty() || ln.isEmpty()) {
                    error = "Merci de renseigner le prénom et le nom."
                    return@onClick
                }
                if (em.isEmpty() || !em.contains('@')) {
                    error = "Merci d’indiquer une adresse e-mail valide."
                    return@onClick
                }
                scope.launch {
                    busy = true
                    error = null
                    try {
                        if (saveUserProfileIdentity(client, fn, ln, em)) {
                            onComplete()
                        } else {
                            error = "Enregistrement impossible. Vérifie la connexion ou que la migration SQL a bien été appliquée sur Supabase."
                        }
                    } finally {
                        busy = false
                    }
                }
            }
            style { parentIdentitySubmit() }
        }) { Text(if (busy) "Enregistrement…" else "Continuer") }
    }
}

private fun StyleScope.parentIdentityCard() {
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    alignItems(AlignItems.Stretch)
    gap(12.px)
    width(100.percent)
    padding(8.px)
}

private fun StyleScope.parentIdentityHeroWrap() {
    display(DisplayStyle.Flex)
    justifyContent(JustifyContent.Center)
    width(100.percent)
    margin(4.px)
}

private fun StyleScope.parentIdentityHeroImg() {
    width(180.px)
    maxWidth(68.percent)
    property("aspect-ratio", "1 / 1")
    property("object-fit", "cover")
    property("border-radius", "22px")
    property("box-shadow", "0 12px 40px rgba(250, 204, 21, 0.22)")
    property("border", "1px solid rgba(255,255,255,0.2)")
}

private fun StyleScope.parentIdentityTitle() {
    color(Color.white)
    fontSize(24.px)
    margin(4.px)
    property("font-weight", "800")
}

private fun StyleScope.parentIdentityLabel() {
    color(Color("#E2E8F0"))
    fontSize(15.px)
    margin(4.px)
    property("font-weight", "600")
}

private fun StyleScope.parentIdentityInput() {
    width(100.percent)
    padding(12.px)
    backgroundColor(Color("rgba(255, 255, 255, 0.10)"))
    color(Color.white)
    property("border", "1px solid rgba(255,255,255,0.22)")
    property("border-radius", "14px")
    property("box-sizing", "border-box")
    property("font-size", "16px")
}

private fun StyleScope.parentIdentitySubmit() {
    width(100.percent)
    margin(8.px)
    padding(14.px)
    property("min-height", "52px")
    color(Color.white)
    backgroundColor(Color("#0EA5E9"))
    property("border", "1px solid #7DD3FC")
    property("border-radius", "14px")
    property("font-weight", "700")
    property("font-size", "16px")
}

private fun StyleScope.parentIdentityError() {
    color(Color("#FCA5A5"))
    fontSize(14.px)
    margin(4.px)
    property("line-height", "1.4")
}
